import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Icons } from './Icons';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success';
  isLoading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  isLoading = false,
  icon,
  children,
  className,
  disabled,
  ...props
}) => {
  const variantStyles = {
    primary:
      'bg-[#FF732E] text-white font-semibold text-base tracking-tight rounded-md hover:bg-[#FF8A4D] hover:shadow-lg hover:shadow-[#FF732E]/20 active:from-[#E65A1F] active:to-[#FF732E] transition-all duration-200',
    secondary:
      'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
    success:
      'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-600/20',
  };

  const isDisabled = disabled || isLoading;

  return (
    <button
      type="button"
      disabled={isDisabled}
      className={twMerge(
        'group relative mx-auto mt-4 inline-flex w-[85%] max-w-md items-center justify-center gap-1.5 px-6 py-2.5 font-medium transition-all',
        variantStyles[variant],
        isDisabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="opacity-0">{children}</span>
          <div className="absolute inset-0 flex items-center justify-center">
            <Icons.Spinner className="h-5 w-5" />
          </div>
        </>
      ) : (
        <>
          {icon}
          {children}
          {variant === 'primary' && (
            <Icons.ArrowRight className="transform transition-transform group-hover:translate-x-1" />
          )}
        </>
      )}
    </button>
  );
};

interface IconButtonProps extends ButtonProps {
  icon: React.ReactNode;
  direction?: 'left' | 'right';
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  direction = 'right',
  ...props
}) => {
  return (
    <Button icon={direction === 'left' ? icon : undefined} {...props}>
      {props.children}
      {direction === 'right' && icon}
    </Button>
  );
};
